import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type Blueprint } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { AppRootContextProvider } from '@r-app/app-root-context-provider';
import { BlueprintSvgContextMenuWrapper } from './blueprint-svg-context-menu-wrapper';
import { ComponentController } from '@r-core/controller';
import { computeFittedViewBox } from './compute-fitted-viewbox';
import { CreateBlueprintSymbols } from '../../symbols';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { DisplayPanelMode } from '../../display-mode/display-panel-mode';
import { usePageFontFactorOverride, useReportComputedFontFactor } from './page-font-factor-context';
import { useSheetDimensionVisibility } from './use-sheet-dimension-visibility.hook';
import { useStabilizedInkExtent } from './use-stabilized-ink-extent.hook';

export type BlueprintPanelCardProps = {
	blueprint: Blueprint;
	islandId: string;
	panelId: string;
	pageIndex: number;
};

/**
 * BlueprintPanelCard renders panel(s) in an isolated Manufacturing viewport.
 * Scale is fixed at 1:12 (model-inch to page-inch) to match the original PDF
 * blueprints. If the panel + dimension annotations exceed the container at 1:12,
 * the content scales down via preserveAspectRatio="meet" to fit.
 */
const BlueprintPanelCardInner = (props: BlueprintPanelCardProps): React.ReactElement => {
	const { blueprint, islandId, panelId, pageIndex } = props;

	// Read the main controller's font factor so blueprint view matches regular views
	const mainContext = useContext(AppContext);
	const baseFactor = mainContext?.controller?.options?.dimensionFontSizeFactor ?? 1.2;

	const svgRef = useRef<SVGSVGElement>(null);
	const contentGroupRef = useRef<SVGGElement>(null);
	const [containerPx, setContainerPx] = useState<{ w: number; h: number }>({ w: 800, h: 400 });

	// Per-page override (from BlueprintPageFontControl). When set, it replaces
	// the controller-derived factor entirely so the user has direct control.
	const pageFontFactorOverride = usePageFontFactorOverride();
	const reportComputedFactor = useReportComputedFontFactor();

	// Font factor — no zoom compensation, user controls via per-page stepper.
	const computedFontFactor = baseFactor;
	const fontFactor = pageFontFactorOverride ?? computedFontFactor;

	// Report the computed default to the per-page font control so the stepper
	// starts from the actual displayed value; effect-deferred (setState in the control mid-render is illegal).
	useEffect(() => {
		reportComputedFactor(computedFontFactor);
	}, [reportComputedFactor, computedFontFactor]);

	// Create controller ONCE per (blueprint, island, panel, pageIndex). Font factor
	// is updated imperatively below to avoid recreating the controller (and its
	// subscriptions / child components) on every font-factor change.
	//
	// The BP view's controller is meant to render exactly like the regular MFG
	// view of the same panel — same options, same auto-hide behavior on
	// intersection dimensions. Mirror every option from the main controller so
	// any user customisation (showApplianceDimensions, showDimensionsMode, etc.)
	// flows through to this isolated viewport.
	const mainOptions = mainContext?.controller?.options;
	const controller = useMemo((): ComponentController => {
		const ctrl = new ComponentController(blueprint, {
			eventGroup: `bp-panel-${pageIndex}-${blueprint.id}`
		});

		ctrl.navigation.displayMode = DisplayMode.Manufacturing;
		ctrl.navigation.islandId = islandId;
		ctrl.navigation.panelId = panelId;

		// Mirror every relevant display option from the main controller so the
		// BP view renders identically to the regular MFG view (intersection
		// auto-hide, dimension mode, breakpoints, etc.).
		if (mainOptions) {
			const mirrored: Array<keyof typeof mainOptions> = [
				'showDimensions', 'showApplianceDimensions', 'showAllDimensions',
				'showDimensionsMode', 'showCutouts', 'showAppliances',
				'showApplianceLabels', 'showBreakpoints', 'showCorners',
				'showCountertops', 'showHints', 'showIslandBaseDimensions',
				'showOverheadDimensions', 'showOverlap', 'showObstructions',
				'showBacksplashes', 'showPanelInsideDimensions',
				'showPanelOutsideDimensions', 'showApplianceGroups',
				'highlightFrontPanel', 'dimensionOrientationMode',
				'applianceOpacity', 'cutoutOpacity', 'borderColor',
				'cutoutColor', 'backgroundColor', 'useFractions',
				'assetsBaseUrl', 'assetsSasToken'
			] as const;
			mirrored.forEach((key) => {
				const value = (mainOptions as unknown as Record<string, unknown>)[key];
				if (value !== undefined) {
					(ctrl.options as unknown as Record<string, unknown>)[key] = value;
				}
			});
		}

		// BP-view specific overrides — these always differ from the main view.
		ctrl.options.isBlueprintView = true;
		ctrl.options.readonly = false;       // allow dimension dragging + context menu
		ctrl.options.contextMenu = true;
		ctrl.options.backgroundColor = '#FFFFFF'; // white cutouts on white page background
		ctrl.options.dimensionFontSizeFactor = fontFactor;
		ctrl.options.dimensions = { fontSizeFactor: fontFactor };

		return ctrl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blueprint, islandId, panelId, pageIndex]);

	// Update the font factor synchronously during render (not in useEffect) so
	// the SVG content this render produces uses the new value. Doing this in an
	// effect would mean the first render still uses the old value, requiring a
	// second click to see the change.
	useSheetDimensionVisibility(controller);

	if (controller.options.dimensionFontSizeFactor !== fontFactor) {
		controller.options.dimensionFontSizeFactor = fontFactor;
		controller.options.dimensions = { fontSizeFactor: fontFactor };
	}

	const { kitchen } = controller;
	const island = kitchen?.design.islands.findById(islandId);
	const panel = island?.panels.findById(panelId);

	// Scale is driven by the PANEL geometry only — never the appliances or
	// dimension annotations drawn on it — so identical panels (e.g. front and
	// back) render the same size regardless of how much annotation each carries.
	const panelTrueWidth = panel && island
		? (panel.isSidePanel ? island.islandSize.depth : panel.size.width)
		: 60;
	const panelTrueHeight = panel?.size.height ?? island?.islandSize.height ?? 34.5;

	// Measure the SVG container's pixel dimensions using ResizeObserver
	useEffect(() => {
		const el = svgRef.current;
		if (!el) return;

		const update = (): void => {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				setContainerPx({ w: rect.width, h: rect.height });
			}
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const inkExtent = useStabilizedInkExtent({
		groupRef: contentGroupRef,
		resetKey: `${islandId}|${panelId}|${pageIndex}|${fontFactor}`
	});

	// Ruler scale against exact panel geometry first; the measured annotation ink
	// only ever shrinks it, and only when it would otherwise cross the page edge.
	const viewBox = useMemo((): string => {
		return computeFittedViewBox({
			geometry: {
				left: -panelTrueWidth / 2,
				right: panelTrueWidth / 2,
				top: -panelTrueHeight,
				bottom: 0
			},
			ink: inkExtent,
			containerPx
		});
	}, [containerPx, panelTrueWidth, panelTrueHeight, inkExtent]);

	useEffect(() => {
		return () => { controller?.destroy(); };
	}, [controller]);

	return (
		<AppRootContextProvider controller={controller}>
			<BlueprintSvgContextMenuWrapper svgRef={svgRef}>
				<svg
					ref={svgRef}
					xmlns="http://www.w3.org/2000/svg"
					style={{ width: '100%', height: '100%', display: 'block', background: 'white' }}
					viewBox={viewBox}
					preserveAspectRatio="xMidYMid meet"
				>
					<CreateBlueprintSymbols />
					<g ref={contentGroupRef}>
						<DisplayPanelMode />
					</g>
				</svg>
			</BlueprintSvgContextMenuWrapper>
		</AppRootContextProvider>
	);
};

export const BlueprintPanelCard = React.memo(
	BlueprintPanelCardInner,
	(prev, next) =>
		prev.islandId === next.islandId
		&& prev.panelId === next.panelId
		&& prev.pageIndex === next.pageIndex
);
